import { ASIC_PRODUCTS } from './asic-product';

export const COMPLAINANT_TYPE = {
    INDIVIDUAL: 'Individual or couple',
    SMALL_BUSINESS: 'Small business',
    OTHER: 'Other',
    UNKNOWN: 'Unknown',
} as const;

/**
 * Literal-typed label tuples for the ASIC IDR complaint vocabularies. These are
 * the single source the option arrays below are built from, and they feed
 * `z.enum(...)` on the Service API write schemas so external payloads can only
 * carry values the ASIC report can resolve to codes.
 */
export const COMPLAINANT_TYPE_LABELS = [
    COMPLAINANT_TYPE.INDIVIDUAL,
    COMPLAINANT_TYPE.SMALL_BUSINESS,
    COMPLAINANT_TYPE.OTHER,
    COMPLAINANT_TYPE.UNKNOWN,
] as const;

export const GENDER_LABELS = ['Male', 'Female', 'Non-binary', 'Unknown or not applicable'] as const;

export const AGE_LABELS = [
    'Less than 18 years',
    '18-24 years',
    '25-34 years',
    '35-44 years',
    '45-54 years',
    '55-64 years',
    '65-74 years',
    '75 years and over',
    'Unknown or not applicable',
] as const;

export const CHANNEL_LABELS = [
    'Face to face',
    'Phone',
    'Email',
    'Web chat',
    'Online form',
    'Social media',
    'Referral from AFCA',
    'Other',
    'Unknown',
] as const;

export const RELATED_TO_AFS_LICENSEE_LABELS = ['Yes', 'No', 'Unknown'] as const;

export const COMPLAINT_STATUS = {
    OPEN: 'open',
    CLOSED: 'closed',
    RE_OPENED: 're-opened',
} as const;

export type ComplaintStatus = (typeof COMPLAINT_STATUS)[keyof typeof COMPLAINT_STATUS];

/**
 * Computed final-response status of a complaint, derived from its lifecycle status
 * and `dtReceived` (deadline = `dtReceived + COMPLAINT_FINAL_RESPONSE_DATE_SETTINGS.DUE_PERIOD_DAYS`).
 * Distinct from the lifecycle `COMPLAINT_STATUS` above. See `getComplaintFinalResponseStatus`.
 */
export const COMPLAINT_FINAL_RESPONSE_STATUSES = {
    NOT_APPLICABLE: 'NOT_APPLICABLE',
    OVERDUE: 'OVERDUE',
    DUE_TODAY: 'DUE_TODAY',
    DUE_3_DAYS: 'DUE_3_DAYS',
    DUE_7_DAYS: 'DUE_7_DAYS',
    NOT_DUE: 'NOT_DUE',
    CLOSED: 'CLOSED',
} as const;

export type ComplaintFinalResponseStatus =
    (typeof COMPLAINT_FINAL_RESPONSE_STATUSES)[keyof typeof COMPLAINT_FINAL_RESPONSE_STATUSES];

export const COMPLAINT_FINAL_RESPONSE_DATE_SETTINGS = {
    DUE_PERIOD_DAYS: 30,
    DUE_3_DAYS_COUNT: 3,
    DUE_7_DAYS_COUNT: 7,
} as const;

type Option = {
    readonly label: string;
    readonly code: number;
};

export const COMPLAINT_MILESTONES = {
    closed: {
        internalName: 'closed',
        label: 'Closed',
        labelWhenCompleted: 'Closed',
    },
    received: {
        internalName: 'received',
        label: 'Received',
        labelWhenCompleted: 'Received',
    },
    registered: {
        internalName: 'registered',
        label: 'Registered',
        labelWhenCompleted: 'Registered',
    },
    reOpened: {
        internalName: 'reopened',
        label: 'Re-opened',
        labelWhenCompleted: 'Re-opened',
    },
    ownerChanged: {
        internalName: 'owner_changed',
        label: 'Owner changed to {{owner}}',
        labelWhenCompleted: 'Owner changed to {{owner}}',
    },
    acknowledgeReceipt: {
        internalName: 'acknowledge_receipt',
        label: 'Acknowledge receipt',
        labelWhenCompleted: 'Receipt Acknowledged',
    },
    respondToClaimant: {
        internalName: 'respond_to_claimant',
        label: 'Respond to the claimant',
        labelWhenCompleted: 'Responded',
    },
    resolve: {
        internalName: 'resolve',
        label: 'Resolve',
        labelWhenCompleted: 'Resolved',
    },
};

export const COMPLAINANT_TYPES: Array<Option> = [
    { label: COMPLAINANT_TYPE.INDIVIDUAL, code: 1 },
    { label: COMPLAINANT_TYPE.SMALL_BUSINESS, code: 2 },
    { label: COMPLAINANT_TYPE.OTHER, code: 3 },
    { label: COMPLAINANT_TYPE.UNKNOWN, code: 9 },
];

export const GENDERS: Array<Option> = [
    { label: GENDER_LABELS[0], code: 1 },
    { label: GENDER_LABELS[1], code: 2 },
    { label: GENDER_LABELS[2], code: 3 },
    { label: GENDER_LABELS[3], code: 9 },
];

export const AGES: Array<Option> = [
    { label: AGE_LABELS[0], code: 1 },
    { label: AGE_LABELS[1], code: 2 },
    { label: AGE_LABELS[2], code: 3 },
    { label: AGE_LABELS[3], code: 4 },
    { label: AGE_LABELS[4], code: 5 },
    { label: AGE_LABELS[5], code: 6 },
    { label: AGE_LABELS[6], code: 7 },
    { label: AGE_LABELS[7], code: 8 },
    { label: AGE_LABELS[8], code: 9 },
];

export const STATUSES: Array<Option> = [
    { label: COMPLAINT_STATUS.OPEN, code: 1 },
    { label: COMPLAINT_STATUS.RE_OPENED, code: 1 },
    { label: COMPLAINT_STATUS.CLOSED, code: 2 },
];

export const CHANNELS: Array<Option> = [
    { label: CHANNEL_LABELS[0], code: 1 },
    { label: CHANNEL_LABELS[1], code: 2 },
    { label: CHANNEL_LABELS[2], code: 3 },
    { label: CHANNEL_LABELS[3], code: 4 },
    { label: CHANNEL_LABELS[4], code: 5 },
    { label: CHANNEL_LABELS[5], code: 6 },
    { label: CHANNEL_LABELS[6], code: 7 },
    { label: CHANNEL_LABELS[7], code: 8 },
    { label: CHANNEL_LABELS[8], code: 9 },
];

export const RELATED_TO_AFS_LICENSEE: Array<Option> = [
    { label: RELATED_TO_AFS_LICENSEE_LABELS[0], code: 1 },
    { label: RELATED_TO_AFS_LICENSEE_LABELS[1], code: 2 },
    { label: RELATED_TO_AFS_LICENSEE_LABELS[2], code: 9 },
];

export const COMPLAINT_OUTCOMES = [
    'Service-based remedy',
    'Monetary remedy',
    'Contract/policy variation',
    'Decision changed',
    'Other remedy',
    'Withdrawn/discontinued',
    'Referred to another financial firm',
    'No remedy provided/ apology or explanation only',
] as const;

export const COMPLAINT_OUTCOME_CODES = {
    'Service-based remedy': '1',
    'Monetary remedy': '2',
    'Contract/policy variation': '3',
    'Decision changed': '4',
    'Other remedy': '5',
    'Withdrawn/discontinued': '6',
    'Referred to another financial firm': '7',
    'No remedy provided/ apology or explanation only': '8',
} as const satisfies Record<ComplaintOutcome, string>;

export type ComplaintOutcome = (typeof COMPLAINT_OUTCOMES)[number];

export const ASIC_COMPLAINT_OUTCOME_CONFIGURATION = {
    'Service-based remedy': {
        code: '1',
        canHaveAdditionalOutcomes: true,
    },
    'Monetary remedy': {
        code: '2',
        canHaveAdditionalOutcomes: true,
    },
    'Contract/policy variation': {
        code: '3',
        canHaveAdditionalOutcomes: true,
    },
    'Decision changed': {
        code: '4',
        canHaveAdditionalOutcomes: true,
    },
    'Other remedy': {
        code: '5',
        canHaveAdditionalOutcomes: true,
    },
    'Withdrawn/discontinued': {
        code: '6',
        canHaveAdditionalOutcomes: false,
    },
    'Referred to another financial firm': {
        code: '7',
        canHaveAdditionalOutcomes: false,
    },
    'No remedy provided/ apology or explanation only': {
        code: '8',
        canHaveAdditionalOutcomes: false,
    },
} as const satisfies Record<ComplaintOutcome, { code: string; canHaveAdditionalOutcomes: boolean }>;

export const RELATIONSHIPS = [
    'Insured',
    'Relative / friend',
    'Broker',
    'Power of Attorney',
    'Legal Representative',
    'Executor of an Estate',
    'Service provider',
] as const;

export const COMPLAINANT_SOLUTIONS = ['Apology', 'Compensation', 'Refund', 'Change of decision', 'Free service'];

export const ISSUES: Array<{ category: string; issues: Array<Option> }> = [
    {
        category: 'Advice',
        issues: [
            { label: 'Quality of advice', code: 1 },
            { label: 'Failure to provide advice', code: 2 },
            { label: 'Other advice-related issues', code: 3 },
        ],
    },
    {
        category: 'Charges',
        issues: [
            { label: 'Break costs', code: 4 },
            { label: 'Deductible or excess', code: 5 },
            { label: 'Commissions', code: 6 },
            { label: 'Fees/costs', code: 7 },
            { label: 'Interest', code: 8 },
            { label: 'Foreign exchange (forex) rate', code: 9 },
            { label: 'Premiums', code: 10 },
            { label: 'Tax', code: 11 },
            { label: 'No claim bonus', code: 12 },
            { label: 'Other charges-related issues', code: 13 },
        ],
    },
    {
        category: 'Consumer Data Right (CDR)',
        issues: [
            { label: 'CDR data collection', code: 14 },
            { label: 'CDR data use or disclosure', code: 15 },
            { label: 'CDR data maintenance', code: 16 },
            {
                label: 'CDR security and destruction/de-identification',
                code: 17,
            },
            { label: 'CDR data correction', code: 18 },
            { label: 'CDR advice', code: 19 },
            { label: 'Other CDR-related issues', code: 20 },
        ],
    },
    {
        category: 'Disclosure',
        issues: [
            { label: 'Break costs disclosure', code: 26 },
            { label: 'Fee disclosure', code: 27 },
            { label: 'Failure to provide disclosure documents', code: 28 },
            { label: 'Product/service information', code: 29 },
            { label: 'Other disclosure-related issues', code: 30 },
        ],
    },
    {
        category: 'Financial difficulty/debt collection',
        issues: [
            {
                label: 'Financial firm failure to respond to request for assistance',
                code: 31,
            },
            { label: 'Decline of financial difficulty request', code: 32 },
            { label: 'Financial difficulty arrangement', code: 33 },
            { label: 'Default judgment obtained', code: 34 },
            { label: 'Default notice', code: 35 },
            { label: 'Request to suspend enforcement proceedings', code: 36 },
            { label: 'Disputed liability', code: 37 },
            { label: 'Initial debt collection contact', code: 38 },
            { label: 'Handling of debt collection', code: 39 },
            {
                label: 'Other issues relating to financial difficulty/debt collection',
                code: 40,
            },
        ],
    },
    {
        category: 'Decision related to insurance',
        issues: [
            { label: 'Insurance coverage terms', code: 45 },
            { label: 'Cancellation of policy', code: 46 },
            { label: 'Claim amount', code: 47 },
            { label: 'Denial of claim—Complainant non-disclosure', code: 48 },
            { label: 'Denial of claim—Driving under influence', code: 49 },
            { label: 'Denial of claim—Exclusion/condition', code: 50 },
            { label: 'Denial of claim—Fraudulent claim', code: 51 },
            { label: 'Denial of claim—No policy or contract', code: 52 },
            { label: 'Denial of claim—No proof of loss', code: 53 },
            { label: 'Denial of claim—Other', code: 54 },
        ],
    },
    {
        category: 'Decision - general',
        issues: [
            {
                label: 'Interpretation of product terms and conditions',
                code: 58,
            },
            { label: 'Unfair contract terms', code: 59 },
            { label: 'Unconscionable conduct', code: 60 },
            { label: 'Denial of application or variation request', code: 61 },
            {
                label: 'Margin call notice and/or investment liquidation',
                code: 62,
            },
            { label: 'Valuation', code: 63 },
            { label: 'Commercial practice or policy', code: 64 },
            { label: 'Other decision-related issues', code: 65 },
        ],
    },
    {
        category: 'Instructions',
        issues: [
            { label: 'Delay in following instructions', code: 66 },
            { label: 'Failure to follow instructions/agreement', code: 67 },
            { label: 'Other instructions-related issues', code: 68 },
        ],
    },
    {
        category: 'Privacy & confidentiality',
        issues: [
            { label: 'Failure/refusal to provide access', code: 70 },
            { label: 'Unauthorised information disclosed', code: 71 },
            {
                label: 'Other issues relating to privacy and/or confidentiality',
                code: 72,
            },
        ],
    },
    {
        category: 'Scams/fraud',
        issues: [
            { label: 'Victim of scam', code: 73 },
            { label: 'Fraud/forgery by financial firm', code: 74 },
            { label: 'Fraud/forgery by a third party', code: 75 },
            { label: 'Other scam/fraud-related issues', code: 76 },
        ],
    },
    {
        category: 'Service',
        issues: [
            { label: 'Account administration error', code: 77 },
            { label: 'Delay in claim handling', code: 78 },
            { label: 'Delay in complaint handling', code: 79 },
            { label: 'General service delay', code: 80 },
            { label: 'Failure to provide special needs assistance', code: 81 },
            { label: 'Incorrect financial information provided', code: 82 },
            { label: 'Loss of documents/personal property', code: 83 },
            { label: 'Management of complainant details', code: 84 },
            { label: 'Technical problems', code: 85 },
            { label: 'Branch closure or opening hours', code: 86 },
            { label: 'Loyalty or reward points', code: 87 },
            { label: 'Other service-related issues', code: 88 },
        ],
    },
    {
        category: 'Transactions',
        issues: [
            { label: 'Chargebacks—Declined (consumer)', code: 89 },
            { label: 'Chargebacks—Delayed (consumer)', code: 90 },
            { label: 'Chargebacks—Merchant', code: 91 },
            { label: 'Dishonoured transactions', code: 92 },
            { label: 'Incorrect payment', code: 93 },
            { label: 'Mistaken internet payment', code: 94 },
            { label: 'Unauthorised transaction', code: 95 },
            { label: 'Other transaction-related issues', code: 96 },
        ],
    },
];

export const COMPLAINT_STATISTIC_SETTINGS = {
    COMPLAINTS_PER_USER_STEPS: [0, 2],
    COMPLAINTS_OPEN_MORE_THAN_30_DAYS_PERCENTAGE_STEPS: [50, 80],
} as const;

// ─── Issue / Product resolution helpers ─────────────────────────────────────

/** Flattened issues with composite labels — built once at module level */
export const ISSUES_FLATTENED: Array<{
    compositeLabel: string;
    displayLabel: string;
    code: number;
}> = ISSUES.flatMap((cat) =>
    cat.issues.map((i) => ({
        compositeLabel: `${cat.category} - ${i.label}`,
        displayLabel: `${cat.category} > ${i.label}`,
        code: i.code,
    })),
);

/** Lookup maps built once for O(1) resolution */
const issueByCompositeLabel = new Map(ISSUES_FLATTENED.map((i) => [i.compositeLabel, i]));
const issueByCode = new Map(ISSUES_FLATTENED.map((i) => [i.code, i]));
const productByCode = new Map(ASIC_PRODUCTS.map((p) => [p.code, p]));
const productByLabel = new Map(ASIC_PRODUCTS.map((p) => [p.label, p]));

/**
 * Resolve a stored issue value to a display label (`"Category > Label"`).
 *
 * Handles all storage shapes:
 * 1. Composite label string — `"Charges - Commissions"` (UI Cascader)
 * 2. Numeric string — `"6"` (bulk import)
 * 3. Raw number — `6`
 */
export function resolveIssueDisplayLabel(value: string | number): string {
    if (typeof value === 'string') {
        const byLabel = issueByCompositeLabel.get(value);
        if (byLabel) return byLabel.displayLabel;

        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            const byCode = issueByCode.get(parsed);
            if (byCode) return byCode.displayLabel;
        }

        return `Unknown (${value})`;
    }

    const byCode = issueByCode.get(value);
    return byCode ? byCode.displayLabel : `Unknown (${value})`;
}

/**
 * Resolve an issue input to its canonical composite label (`"Category - Label"`),
 * or null when unresolvable. Accepts the composite form itself and numeric ASIC
 * issue codes (number or numeric string). The ASIC IDR export resolves issues by
 * exact composite label only, so write boundaries (Service API) must persist the
 * value this function returns — a stored numeric code would export as `999`.
 */
export function resolveIssueCompositeLabel(value: string | number): string | null {
    if (typeof value === 'string') {
        if (issueByCompositeLabel.has(value)) return value;
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return issueByCode.get(parsed)?.compositeLabel ?? null;
        return null;
    }
    return issueByCode.get(value)?.compositeLabel ?? null;
}

/** Validate a stored issue value against known codes/labels */
export function isValidIssue(value: string | number): boolean {
    if (typeof value === 'string') {
        if (issueByCompositeLabel.has(value)) return true;
        const parsed = Number(value);
        return !Number.isNaN(parsed) && issueByCode.has(parsed);
    }
    return issueByCode.has(value);
}

/**
 * Resolve a stored product value to a display label.
 *
 * Handles numeric codes (primary) and legacy label strings.
 */
export function resolveProductDisplayLabel(value: string | number): string {
    if (typeof value === 'number') {
        const byCode = productByCode.get(value);
        return byCode ? byCode.label : `Unknown (${value})`;
    }

    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
        const byCode = productByCode.get(parsed);
        if (byCode) return byCode.label;
    }

    const byLabel = productByLabel.get(value);
    if (byLabel) return byLabel.label;

    return `Unknown (${value})`;
}

/** Validate a stored product value against known codes/labels */
export function isValidProduct(value: string | number): boolean {
    if (typeof value === 'number') return productByCode.has(value);

    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return productByCode.has(parsed);

    return productByLabel.has(value);
}

/**
 * Max length of a complaint's incident description on creation. Enforced by the
 * add form (counter + maxLength) and the createComplaint schema. Update paths
 * are deliberately uncapped — legacy records exceed this.
 */
export const COMPLAINT_INCIDENT_DESCRIPTION_MAX_LENGTH = 3000;

/**
 * Coverage-checklist keys, in the order shown under the complaint's incident
 * description on the create form ("What your description covers"). Separate check
 * set from incidents. The `key`s are the stable client/server contract for AI
 * coverage suggestions and back the CUR-317 description-quality metric (the create
 * form persists which of these the AI marked covered — see `descriptionCoverage`).
 */
export const COMPLAINT_COVERAGE_KEYS = ['what_happened', 'when', 'how_affected', 'desired_outcome'] as const;

export type ComplaintCoverageKey = (typeof COMPLAINT_COVERAGE_KEYS)[number];

/** Chip label per coverage key, in display order. */
export const COMPLAINT_COVERAGE_ITEMS = [
    { key: 'what_happened', label: 'What happened?' },
    { key: 'when', label: 'When did it happen?' },
    { key: 'how_affected', label: 'How was the complainant affected?' },
    { key: 'desired_outcome', label: 'What outcome do they want?' },
] as const satisfies ReadonlyArray<{ key: ComplaintCoverageKey; label: string }>;

// ─── Postcode validation (ASIC IDR) ──────────────────────────────────────────

/** Valid special postcode values for ASIC IDR reporting */
export const COMPLAINANT_POSTCODE_SPECIAL_VALUES = ['Overseas', 'Not applicable'] as const;
export type ComplainantPostcodeSpecialValue = (typeof COMPLAINANT_POSTCODE_SPECIAL_VALUES)[number];

/** Regex for a valid 4-digit Australian postcode */
export const AUSTRALIAN_POSTCODE_REGEX = /^\d{4}$/;

/** Validates a complainant postcode value for ASIC IDR compliance. */
export function isValidComplainantPostcode(value: string | null | undefined): boolean {
    if (value === null || value === undefined || value === '') return true;
    return AUSTRALIAN_POSTCODE_REGEX.test(value) || COMPLAINANT_POSTCODE_SPECIAL_VALUES.some((v) => v === value);
}
